import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, CdkDropListGroup, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAngleDoubleLeft, faAngleDoubleRight, faCheck, faGripLinesVertical, faSitemap, faUser, faUsers } from '@fortawesome/free-solid-svg-icons';
import { Group } from 'src/app/models/group';
import { IViewAccessibility } from 'src/app/models/interfaces/view-accessibility.interface';
import { User } from 'src/app/models/user';
import { View } from 'src/app/models/view';
import { ViewAccessibility } from 'src/app/models/view-accessibility';

@Component({
    selector: 'app-view-accessibilities-management',
    templateUrl: './view-accessibilities-management.component.html',
    styleUrls: ['./view-accessibilities-management.component.scss'],
    imports: [CdkDropListGroup, MatTabsModule, CdkDropList, CdkDrag, FaIconComponent, CdkDragHandle, MatButtonModule],
})
export class ViewAccessibilitiesManagementComponent {
    public faUser = faUser;
    public faUsers = faUsers;
    public faCheck = faCheck;
    public faAngleDoubleLeft = faAngleDoubleLeft;
    public faAngleDoubleRight = faAngleDoubleRight;
    public faGripLinesVertical = faGripLinesVertical;
    public faSitemap = faSitemap;

    @Input()
    public view: View;

    /**
     * Valeur indiquant si le formulaire est disabled.
     */
    @Input()
    public disabled: boolean;

    @Output()
    public accessibilitiesUpdated: EventEmitter<ViewAccessibility[]> = new EventEmitter();

    @Input()
    users: User[];

    @Input()
    unusedUsers: User[];

    @Input()
    groups: Group[];

    @Input()
    unusedGroups: Group[];

    @Input()
    accessibilities: ViewAccessibility[];

    @Input()
    originalAccessibilities: ViewAccessibility[];

    @Input()
    inheritedAccessibilities: ViewAccessibility[];

    @ViewChild(MatTabGroup) tabGroup: MatTabGroup;

    constructor() {}

    /**
     * Supprime un accès des listes
     */
    removeAccessibility(accessibility: IViewAccessibility, pos: number) {
        let index: number;

        if (accessibility.userId) {
            index = this.accessibilities.findIndex((x) => x.userId === accessibility.userId);
        } else {
            index = this.accessibilities.findIndex((x) => x.groupId === accessibility.groupId);
        }

        let accessibilities = [...this.accessibilities];

        if (!accessibility.isInherited) {
            if (accessibility.userId && this.inheritedAccessibilities && this.inheritedAccessibilities.some((x) => x.userId === accessibility.userId)) {
                // Il existe un héritage pour la vue dont l'accès a été retiré --> l'accès hérité est ajouté à la liste

                accessibilities.splice(index, 1);

                accessibilities.push(this.inheritedAccessibilities.find((x) => x.userId === accessibility.userId));
                this.sortAccessibilities(accessibilities);
            } else if (accessibility.groupId && this.inheritedAccessibilities && this.inheritedAccessibilities.some((x) => x.groupId === accessibility.groupId)) {
                // Il existe un héritage pour la vue dont l'accès a été retiré --> l'accès hérité est ajouté à la liste

                accessibilities.splice(index, 1);

                accessibilities.push(this.inheritedAccessibilities.find((x) => x.groupId === accessibility.groupId));
                this.sortAccessibilities(accessibilities);
            } else if (accessibility.userId) {
                accessibilities.splice(index, 1);
                let user = this.users.find((x) => x.id === accessibility.userId);
                this.unusedUsers.push(user);
                this.sortUnusedUsers();
            } else if (accessibility.groupId) {
                accessibilities.splice(index, 1);
                let group = this.groups.find((x) => x.id === accessibility.groupId);
                this.unusedGroups.push(group);
                this.sortUnusedGroups();
            }

            this.accessibilities = accessibilities;
            this.accessibilitiesUpdated.emit(this.accessibilities);
        }
    }

    /**
     * Ajout un utilisateur à la liste
     * */
    addUserAccessibility(userId: string, index: number) {
        // Dans le cas où l'accès était présent à l'origine, on récupère l'id pour le mettre à jour
        const originalId = this.originalAccessibilities.find((x) => x.userId === userId)?.id;

        const viewAccessibility: ViewAccessibility[] = [
            {
                id: originalId,
                viewId: this.view.id,
                userId: userId,
                groupId: null,
                viewName: this.view.name,
                userName: this.getUserName(userId),
                groupName: null,
                isInherited: false,
            },
        ];

        let accessibilities = [...this.accessibilities];

        transferArrayItem<IViewAccessibility>(viewAccessibility, accessibilities, -1, index);

        this.sortAccessibilities(accessibilities);
        this.accessibilities = accessibilities;
        this.accessibilitiesUpdated.emit(this.accessibilities);
    }

    /**
     * Ajout un groupe à la liste
     */
    addGroupAccessibility(groupId: string, index: number) {
        // Dans le cas où l'accès était présent à l'origine, on récupère l'id pour le mettre à jour
        const originalId = this.originalAccessibilities.find((x) => x.groupId === groupId)?.id;

        const viewAccessibility: ViewAccessibility[] = [
            {
                id: originalId,
                viewId: this.view.id,
                userId: null,
                groupId: groupId,
                viewName: this.view.name,
                userName: null,
                groupName: this.getGroupName(groupId),
                isInherited: false,
            },
        ];

        let accessibilities = [...this.accessibilities];

        transferArrayItem<IViewAccessibility>(viewAccessibility, accessibilities, -1, index);

        this.sortAccessibilities(accessibilities);
        this.accessibilities = accessibilities;
        this.accessibilitiesUpdated.emit(this.accessibilities);
    }

    /**
     * Supprime toutes les accessibilité.
     */
    public removeAllAccessibilities(): void {
        let remainingAccessibilities = [];
        this.accessibilities.forEach((accessibility) => {
            if (accessibility.userId) {
                this.moveUserInUnusedUsers(accessibility, remainingAccessibilities);
            } else {
                this.moveGroupInUnusedGroups(accessibility, remainingAccessibilities);
            }
        });

        this.accessibilities = remainingAccessibilities;
        this.sortAccessibilities(this.accessibilities);
        this.sortUnusedUsers();
        this.sortUnusedGroups();
        this.accessibilitiesUpdated.emit(this.accessibilities);
    }

    private moveUserInUnusedUsers(accessibility: ViewAccessibility, remainingAccessibilities: any[]) {
        if (!accessibility.isInherited) {
            if (this.inheritedAccessibilities && this.inheritedAccessibilities.some((x) => x.userId === accessibility.userId) === true) {
                // Il existe un héritage pour l'utilisateur dont l'accès a été retiré --> l'accès hérité est ajouté à la liste
                let index = this.accessibilities.findIndex((x) => x.userId === accessibility.userId);

                remainingAccessibilities.push(this.inheritedAccessibilities.find((x) => x.userId === accessibility.userId));
            } else {
                this.unusedUsers.push(this.getUser(accessibility.userId));
            }
        } else {
            remainingAccessibilities.push(accessibility);
        }
    }

    private moveGroupInUnusedGroups(accessibility: ViewAccessibility, remainingAccessibilities: any[]) {
        if (!accessibility.isInherited) {
            if (this.inheritedAccessibilities && this.inheritedAccessibilities.some((x) => x.groupId === accessibility.groupId) === true) {
                // Il existe un héritage pour l'utilisateur dont l'accès a été retiré --> l'accès hérité est ajouté à la liste
                let index = this.accessibilities.findIndex((x) => x.groupId === accessibility.groupId);

                remainingAccessibilities.push(this.inheritedAccessibilities.find((x) => x.groupId === accessibility.groupId));
            } else {
                this.unusedGroups.push(this.getGroup(accessibility.groupId));
            }
        } else {
            remainingAccessibilities.push(accessibility);
        }
    }

    /**
     * Ajout toutes les accessibilité.
     */
    public moveAllUsersInAccessibility(): void {
        let accessibilities = [...this.accessibilities];

        this.unusedUsers.forEach((user) => {
            const viewAccessibility: ViewAccessibility = {
                id: null,
                viewId: this.view.id,
                userId: user.id,
                groupId: null,
                viewName: this.view.name,
                userName: user.displayName,
                groupName: null,
                isInherited: false,
            };

            accessibilities.push(viewAccessibility);
        });

        this.sortAccessibilities(accessibilities);

        this.accessibilities = accessibilities;
        this.accessibilitiesUpdated.emit(this.accessibilities);

        this.unusedUsers = [];
    }

    /**
     * Ajout toutes les accessibilité.
     */
    public moveAllGroupsInAccessibility(): void {
        /******************* TODO !!!!!!!!!!!!!!!!!!!!! ***************************** */
        let accessibilities = [...this.accessibilities];

        this.unusedGroups.forEach((group) => {
            const viewAccessibility: ViewAccessibility = {
                id: null,
                viewId: this.view.id,
                userId: null,
                groupId: group.id,
                viewName: this.view.name,
                userName: null,
                groupName: group.name,
                isInherited: false,
            };

            accessibilities.push(viewAccessibility);
        });

        this.sortAccessibilities(accessibilities);

        this.accessibilities = accessibilities;
        this.accessibilitiesUpdated.emit(this.accessibilities);

        this.unusedGroups = [];
    }

    sortAccessibilities(accessibilities: ViewAccessibility[]) {
        accessibilities = accessibilities.sort((x, y) => {
            if (x.userId) {
                return x.userId.localeCompare(y.userId);
            } else if (x.groupId) {
                return x.groupId.localeCompare(y.groupId);
            } else {
                return -1;
            }
        });
    }

    /**
     * Gestion des movements des boites
     */
    drop(event: CdkDragDrop<string[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else if (event.previousContainer.id === 'unused-users') {
            this.addUserAccessibility((event.previousContainer.data[event.previousIndex] as any as User).id, event.currentIndex);
            event.previousContainer.data.splice(event.previousIndex, 1);
        } else if (event.previousContainer.id === 'unused-groups') {
            this.addGroupAccessibility((event.previousContainer.data[event.previousIndex] as any as Group).id, event.currentIndex);
            event.previousContainer.data.splice(event.previousIndex, 1);
        } else if (event.previousContainer.id === 'accessibilities') {
            let selectedData = (event.previousContainer.data as any[] as IViewAccessibility[])[event.previousIndex];
            this.removeAccessibility(selectedData, event.previousIndex);
        }
    }

    private getUserName(userId: string) {
        const user = this.users.find((x) => x.id == userId);
        return user.displayName;
    }

    private getUser(userId: string) {
        const user = this.users.find((x) => x.id == userId);
        return user;
    }

    private getGroupName(groupId: string) {
        const group = this.groups.find((x) => x.id == groupId);
        return `${group.name}`;
    }

    private getGroup(groupId: string) {
        const group = this.groups.find((x) => x.id == groupId);
        return group;
    }

    private sortUnusedUsers() {
        let sortedUsers = this.unusedUsers.sort((a, b) => {
            return a.displayName < b.displayName ? -1 : 1;
        });

        this.unusedUsers = [...sortedUsers];
    }

    private sortUnusedGroups() {
        let sortedGroups = this.unusedGroups.sort((a, b) => {
            return a.name < b.name ? -1 : 1;
        });

        this.unusedGroups = [...sortedGroups];
    }
}
